/**
 * The branch a signed-in staff member is acting for, shared by the admin
 * screens and picked in the header rather than per page.
 *
 * The choice itself is not sensitive, so unlike the staff JWT it persists in
 * localStorage — staff at a one-branch office should never see a picker step.
 * Defaults, in order: the stored choice, this device's registered branch, the
 * only branch on the account. Multi-branch admins with no history pick once.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { fetchBranches, type Branch } from "../api/kiosk";
import { useDevice } from "./DeviceContext";
import { useStaff } from "./StaffContext";

const STORAGE_KEY = "zebra-kiosk-admin-branch";

const NO_BRANCHES: Branch[] = [];

interface AdminBranchContextValue {
  /** Empty until the staff session's branch list has loaded. */
  branches: Branch[];
  branchId: number | "";
  branchName: string;
  loading: boolean;
  /** Set when the branch list could not be loaded; screens surface it. */
  error: string | null;
  setBranchId: (id: number | "") => void;
}

const AdminBranchContext = createContext<AdminBranchContextValue | null>(null);

function readStoredBranchId(): number | "" {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const id = raw === null ? NaN : Number(raw);
    return Number.isInteger(id) && id > 0 ? id : "";
  } catch {
    return "";
  }
}

function persistBranchId(id: number | ""): void {
  try {
    if (id === "") window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, String(id));
  } catch {
    /* the picker still works, it just forgets on reload */
  }
}

export function AdminBranchProvider({ children }: { children: ReactNode }) {
  const { staff, handleApiError } = useStaff();
  const { device } = useDevice();
  const token = staff?.token ?? "";

  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchId, setBranchIdState] = useState<number | "">(readStoredBranchId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const list = await fetchBranches(token);
        if (cancelled) return;
        setBranches(list);
        setError(null);
        setBranchIdState((current) => {
          const has = (id: number | "") =>
            id !== "" && list.some((branch) => branch.branch_id === id);
          if (has(current)) return current;
          if (device && has(device.branchId)) return device.branchId;
          if (list.length === 1) return list[0].branch_id;
          return "";
        });
      } catch (caught) {
        if (cancelled) return;
        if (handleApiError(caught)) return;
        setError("Could not load your branches.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, device, handleApiError]);

  const setBranchId = useCallback((id: number | "") => {
    setBranchIdState(id);
    persistBranchId(id);
  }, []);

  // Signed out, the list and its errors are the dead session's — hide rather
  // than clear, so the same user signing back in gets the list instantly while
  // the refetch confirms it. The stored *choice* is kept either way.
  const exposedBranches = token ? branches : NO_BRANCHES;
  const exposedError = token ? error : null;

  const branchName = useMemo(
    () =>
      exposedBranches.find((branch) => branch.branch_id === branchId)?.branch_name ?? "",
    [exposedBranches, branchId],
  );

  const value = useMemo<AdminBranchContextValue>(
    () => ({
      branches: exposedBranches,
      branchId,
      branchName,
      loading,
      error: exposedError,
      setBranchId,
    }),
    [exposedBranches, branchId, branchName, loading, exposedError, setBranchId],
  );

  return (
    <AdminBranchContext.Provider value={value}>{children}</AdminBranchContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAdminBranch(): AdminBranchContextValue {
  const context = useContext(AdminBranchContext);
  if (!context) {
    throw new Error("useAdminBranch must be used inside <AdminBranchProvider>");
  }
  return context;
}
